import {
    useTheme,
    Box,
    Typography,
    Paper,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexCenter from 'components/FlexCenter';


const StatCell = ({ label, value, valueColor }) => (
    <Box>
        <Typography 
            variant="caption" 
            color="text.secondary" 
            display="block"
        >
            {label}
        </Typography>
        <Typography 
            variant="body2" 
            fontWeight="medium" 
            color={valueColor ?? 'inherit'}
        >
            {value}
        </Typography>
    </Box>
);

const AnnualLeaveCard = ({ row, index, canView, onViewClick }) => {
    const theme = useTheme();
    const remainingColor = row.remainingALs < 0 ? 'orange' : 'inherit';

    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 1.5, 
                borderLeft: `2px solid ${theme.palette.primary.main}`, 
                borderRadius: 1 
            }}
        >
            <Box display="flex" alignItems="center" gap={1}>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    #{index + 1}
                </Typography>
                <Typography
                    variant="subtitle2"
                    noWrap
                    flex={1}
                    minWidth={0}
                    sx={{ color: row.gender === 'FM' ? theme.palette.secondary.main : theme.palette.primary.dark }}
                >
                    {row.crewCode} {row.crewName}
                </Typography>
                {canView && (
                    <Tooltip title="View roster">
                        <IconButton
                            size="small"
                            onClick={() => onViewClick(row)}
                            sx={{ color: theme.palette.primary.dark, '&:hover': { color: theme.palette.green?.deep } }}
                        >
                            <Visibility fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Box display="flex" gap={0.5} mt={0.75} flexWrap="wrap">
                {row.rank && <Chip label={row.rank} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                {row.location && <Chip label={row.location} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                {row.trainingQuals?.map(q => (
                    <Chip key={q} label={q} size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                ))}
            </Box>

            <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={1} mt={1}>
                <StatCell label="Std ALs" value={row.stdALs > 0 ? parseFloat(row.stdALs).toFixed(1) : 0} />
                <StatCell label="Taken ALs" value={row.takenALs > 0 ? parseFloat(row.takenALs).toFixed(0) : 0} />
                <StatCell label="Remaining" value={parseFloat(row.remainingALs ?? 0).toFixed(1)} valueColor={remainingColor} />
                <StatCell label="LWOP" value={row.takenLWOPs ?? 0} />
            </Box>
        </Paper>
    );
};


const AnnualLeavesList = ({ rows, canView, onViewClick }) => {
    if (!rows?.length) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">No data available</Typography>
            </FlexCenter>
        );
    }

    return (
        <Box            
            display="flex"
            flexDirection="column"
            width="100%"
            gap={2}
        >
            {rows?.map((row, index) => (
                <AnnualLeaveCard 
                    key={row._id} 
                    row={row} 
                    index={index} 
                    canView={canView} 
                    onViewClick={onViewClick} 
                />
            ))}
        </Box>
    );
};

export default AnnualLeavesList;
